import * as Sentry from "@sentry/react";

import { createLogger } from "@/logger";
import { indexedDbAvailable } from "@/store/indexedDbAvailable";
import { awaitOpen, deleteDatabaseBounded } from "@/store/persistence/idbConnection";

const log = createLogger("storage");

const PROBE_DB_NAME = "__storage_probe__";
const PROBE_STORE_NAME = "probe";

const MAX_RANDOM_BYTES = 65536;

function fillRandomBytes(buffer: Uint8Array<ArrayBuffer>): void {
  for (let offset = 0; offset < buffer.length; offset += MAX_RANDOM_BYTES) {
    const slice = buffer.subarray(offset, Math.min(offset + MAX_RANDOM_BYTES, buffer.length));
    crypto.getRandomValues(slice);
  }
}

async function measureIndexedDbWrite(sizeBytes: number): Promise<number> {
  const blob = new Uint8Array(sizeBytes);
  fillRandomBytes(blob);

  const db = await awaitOpen(indexedDB.open(PROBE_DB_NAME, 1), PROBE_DB_NAME, (opened) => {
    if (!opened.objectStoreNames.contains(PROBE_STORE_NAME)) {
      opened.createObjectStore(PROBE_STORE_NAME);
    }
  });

  const start = performance.now();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(PROBE_STORE_NAME, "readwrite");
    tx.objectStore(PROBE_STORE_NAME).put(blob, "probe");
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  const elapsed = performance.now() - start;

  db.close();
  return elapsed;
}

async function cleanup(): Promise<void> {
  if (!indexedDbAvailable()) return;
  await deleteDatabaseBounded(PROBE_DB_NAME);
}

export async function runStorageProbe(): Promise<void> {
  if (!indexedDbAvailable()) {
    Sentry.setContext("storage_io", { error: "probe_unsupported" });
    log.info("[StorageProbe] Skipping — IndexedDB not available");
    return;
  }
  try {
    const write1KbMs = await measureIndexedDbWrite(1024);
    const write100KbMs = await measureIndexedDbWrite(100 * 1024);

    const probeResults = {
      write_1kb_ms: Math.round(write1KbMs * 100) / 100,
      write_100kb_ms: Math.round(write100KbMs * 100) / 100,
    };

    Sentry.setContext("storage_io", probeResults);
    log.info("[StorageProbe] IndexedDB write latency", probeResults);
  } catch (error) {
    log.warn("[StorageProbe] Failed to measure storage I/O", { error });
    Sentry.setContext("storage_io", { error: "probe_failed" });
  } finally {
    await cleanup();
  }
}
